import { CaretDown, CaretRight, Copy } from "@phosphor-icons/react";
import { useState } from "react";

export interface JsonTreeViewProps {
	data: unknown;
	defaultExpandDepth?: number;
}

interface NodeProps {
	value: unknown;
	depth: number;
	defaultExpandDepth: number;
	label?: string;
}

const MAX_STRING_LENGTH = 200;

function copyToClipboard(text: string): void {
	navigator.clipboard.writeText(text);
}

function truncate(s: string): string {
	if (s.length > MAX_STRING_LENGTH) {
		return `${s.slice(0, MAX_STRING_LENGTH)}...`;
	}
	return s;
}

function CopyButton({ value }: { value: string }) {
	return (
		<button
			type="button"
			aria-label="Copy value"
			onClick={(e) => {
				e.stopPropagation();
				copyToClipboard(value);
			}}
			className="ml-1 opacity-0 group-hover:opacity-60 hover:!opacity-100 text-text-muted transition-opacity text-xs px-1 rounded"
		>
			<Copy size={12} />
		</button>
	);
}

function PrimitiveNode({
	value,
	label,
}: { value: string | number | boolean | null; label?: string }) {
	let colorClass: string;
	let displayValue: string;

	if (value === null) {
		colorClass = "text-text-muted";
		displayValue = "null";
	} else if (typeof value === "string") {
		colorClass = "text-cat-ai";
		displayValue = `"${truncate(value)}"`;
	} else if (typeof value === "number") {
		colorClass = "text-cat-transform";
		displayValue = String(value);
	} else {
		// boolean
		colorClass = "text-cat-comms";
		displayValue = String(value);
	}

	const rawValue = value === null ? "null" : String(value);

	return (
		<div className="group flex items-center font-mono text-xs">
			{label !== undefined && <span className="text-text-muted mr-1">{label}:</span>}
			<span className={colorClass}>{displayValue}</span>
			<CopyButton value={rawValue} />
		</div>
	);
}

function JsonNode({ value, depth, defaultExpandDepth, label }: NodeProps) {
	const isExpanded = depth < defaultExpandDepth;
	const [expanded, setExpanded] = useState(isExpanded);

	if (value === null || typeof value !== "object") {
		return <PrimitiveNode value={value as string | number | boolean | null} label={label} />;
	}

	const isArray = Array.isArray(value);
	const entries = isArray
		? (value as unknown[]).map((v, i) => [String(i), v] as [string, unknown])
		: Object.entries(value as Record<string, unknown>);

	const openBracket = isArray ? "[" : "{";
	const closeBracket = isArray ? "]" : "}";

	if (entries.length === 0) {
		return (
			<div className="group flex items-center font-mono text-xs">
				{label !== undefined && <span className="text-text-muted mr-1">{label}:</span>}
				<span className="text-text-muted">
					{openBracket}
					{closeBracket}
				</span>
				<CopyButton value={isArray ? "[]" : "{}"} />
			</div>
		);
	}

	const summary = isArray
		? `${openBracket}${entries.length} item${entries.length === 1 ? "" : "s"}${closeBracket}`
		: `${openBracket}${entries.length} key${entries.length === 1 ? "" : "s"}${closeBracket}`;

	return (
		<div className="font-mono text-xs">
			<button
				type="button"
				onClick={() => setExpanded((prev) => !prev)}
				className="group flex items-center text-left w-full hover:bg-accent-bg rounded px-0.5 -mx-0.5 transition-colors"
			>
				<span className="text-text-muted mr-1 select-none w-3 inline-block">
					{expanded ? <CaretDown size={10} /> : <CaretRight size={10} />}
				</span>
				{label !== undefined && <span className="text-text-muted mr-1">{label}:</span>}
				{!expanded && <span className="text-text-muted">{summary}</span>}
				{expanded && <span className="text-text-muted">{openBracket}</span>}
			</button>
			{expanded && (
				<>
					<div className="pl-4 border-l border-border ml-1.5 mt-0.5 space-y-0.5">
						{entries.map(([key, val]) => (
							<JsonNode
								key={key}
								value={val}
								depth={depth + 1}
								defaultExpandDepth={defaultExpandDepth}
								label={isArray ? undefined : key}
							/>
						))}
					</div>
					<div className="pl-3.5">
						<span className="text-text-muted">{closeBracket}</span>
					</div>
				</>
			)}
		</div>
	);
}

export function JsonTreeView({ data, defaultExpandDepth = 2 }: JsonTreeViewProps) {
	return (
		<div className="font-mono text-xs text-text-primary">
			<JsonNode value={data} depth={0} defaultExpandDepth={defaultExpandDepth} />
		</div>
	);
}
